/*
 * AI 填表员开通弹窗的动态文案接入。
 *
 * 按 VipPaymentModal/CUSTOMIZATION.md 的「渲染器注册表 + customContent」机制接入，
 * 不改弹窗本体。设计稿要点：
 *   - title    「开通 / 升级会员，高效填表少加班」（按是否已开通切换）
 *   - subtitle  未开通 → 固定权益话术；已开通 → 「当前为{等级}，已用 {X}积分」（数字红高亮）
 *   - tabNotes  各会员 tab 名下方「XXw 积分/月」
 *   - cardBanner 选中 tab 商品卡上方橙色横幅「X会员 获赠 Y 积分/月 · 积分共享，永久有效」
 *
 * 注册靠 import 副作用：work / home 静态 import aiClerkPayContent 即完成注册。
 */
import React from 'react'
// 横幅左侧的积分小图标（设计稿在 Figma 757:1462 选中的素材）。
// SVG 走 @svgr/webpack 的 ReactComponent 模式，外层可用 style 调尺寸/对齐。
import PointsBannerIcon from './images/icon-points-banner.svg?react'
import { registerPaymentContent } from '../../components/VipPaymentModal/contentRegistry'
import CONFIG from '../../../constants/config'

/** aiClerk 开通弹窗定制 variant（唯一名，避免与其他接入方撞车） */
export const AICLERK_PAY_VARIANT = 'aiClerkPay'

// 设计稿配色（内容样式内联自管，不依赖弹窗 class）
const RED: React.CSSProperties = { color: '#ea4d3d', fontWeight: 600 } // 副标题已用积分 / 可升级 tab 数字
const GRAY: React.CSSProperties = { color: '#9ca3af', fontWeight: 600 } // 当前等级及以下 tab 数字（不可升级）
const ORANGE: React.CSSProperties = { color: '#f76a07', fontWeight: 600 } // 横幅「获赠 X」
const STRONG: React.CSSProperties = { color: '#444', fontWeight: 600 }

/** vipKind → 等级中文名（已开通副标题用） */
const LEVEL_NAME: Record<number, string> = {
  [CONFIG.VIP_KIND.VIP]: '基础会员',
  [CONFIG.VIP_KIND.VVIP]: '高级会员',
  [CONFIG.VIP_KIND.SVIP]: '超级会员',
  [CONFIG.VIP_KIND.EVIP]: '企业会员'
}

/**
 * vipKind 数值 → 等级序号（值越大等级越高）。
 *
 * ⚠ CONFIG.VIP_KIND 的数值不代表等级（VIP=1 / VVIP=3 / SVIP=2 / EVIP=4），
 *   不能直接 `a > b` 比较；必须经过本表归一化。
 * 顺序按 CLAUDE.md：noVip < VIP（基础）< VVIP（高级）< SVIP（超级）< EVIP（企业）。
 */
const LEVEL_ORDER: Record<number, number> = {
  0: 0, // 非会员
  [CONFIG.VIP_KIND.VIP]: 1, // 基础
  [CONFIG.VIP_KIND.VVIP]: 2, // 高级
  [CONFIG.VIP_KIND.SVIP]: 3, // 超级
  [CONFIG.VIP_KIND.EVIP]: 4 // 企业
}

/** tab key → 等级序号（跟 LEVEL_ORDER 对齐，用于跟当前 vipKind 比较） */
const TAB_LEVEL_ORDER: Record<GiftKey, number> = {
  basic: 1,
  advanced: 2,
  super: 3
}

/** 各会员等级月赠积分（按设计稿；企业级走兜底「更多惊喜」） */
const GIFT = {
  basic: { name: '基础会员', tab: '50w', amount: '50万' },
  advanced: { name: '高级会员', tab: '600w', amount: '600万' },
  super: { name: '超级会员', tab: '3000w', amount: '3000万' }
} as const

type GiftKey = keyof typeof GIFT

/** 万 / 亿格式化（内联自管） */
function formatWan(n: number): string {
  const v = Math.max(0, n || 0)
  if (v >= 1e8) return `${parseFloat((v / 1e8).toFixed(2))}亿`
  if (v >= 1e4) return `${parseFloat((v / 1e4).toFixed(2))}万`
  return v.toLocaleString('en-US')
}

/**
 * tab 名下方小字：「XXw 积分/月」。
 * 数字颜色按是否「高于当前等级」分流：
 *   - 高于当前 → 红色（可升级到这一档，需要让人眼一亮）
 *   - 当前 / 低于 → 灰色（用户已经拥有同等或更高，强调"无需关注"）
 *
 * 例：当前是高级会员(VVIP)，弹窗里基础(50w)和高级(600w)都灰，超级(3000w)红。
 */
function tabNote(k: GiftKey, isHigher: boolean) {
  return (
    <span>
      <span style={isHigher ? RED : GRAY}>{GIFT[k].tab}</span> 积分/月
    </span>
  )
}

/**
 * 商品卡上方橙色横幅：「[icon] X会员 获赠 Y 积分/月 · 积分共享，永久有效」（设计稿 757:1462）。
 * 领导的 .panel-card-banner 容器只给宽度，橙底 / 圆角 / 间距按 CUSTOMIZATION.md 由业务内联。
 */
const BANNER_BOX: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: 38,
  padding: '6px 20px',
  borderRadius: 30,
  background: '#fcf6ef',
  fontSize: 16,
  boxSizing: 'border-box'
}
const BANNER_ICON: React.CSSProperties = {
  width: 22,
  height: 22,
  flexShrink: 0,
  marginRight: 2
}
function cardBanner(k: GiftKey) {
  return (
    <div style={BANNER_BOX}>
      <PointsBannerIcon style={BANNER_ICON} aria-hidden="true" />
      <span style={STRONG}>{GIFT[k].name}</span>
      <span style={ORANGE}>{`获赠 ${GIFT[k].amount} 积分/月`}</span>
      <span style={{ color: '#444' }}>积分共享，永久有效</span>
    </div>
  )
}

/** customContent.data —— 仅可序列化字段（不含 JSX / 函数） */
export interface AiClerkPayData {
  /** 是否已开通会员 → 主标题「升级」/「开通」+ 副标题动态/固定 */
  isVip: boolean
  /** 当前会员等级（CONFIG.VIP_KIND 值），已开通副标题「当前为 X 会员」用 */
  vipKind: number
  /** 已消耗积分，已开通副标题「已用 X 积分」用 */
  usedPoints: number
}

registerPaymentContent(AICLERK_PAY_VARIANT, (d: AiClerkPayData) => {
  // 当前等级序号(noVip / VIP / VVIP / SVIP / EVIP → 0..4)，用于判断每个 tab 是否
  // 「高于当前等级」(可升级)。LEVEL_ORDER[unknown] → undefined → 比较为 false 兜底。
  const currentOrder = LEVEL_ORDER[d.vipKind] ?? 0
  const isHigher = (k: GiftKey): boolean => TAB_LEVEL_ORDER[k] > currentOrder
  return {
    title: `${d.isVip ? '升级' : '开通'}会员，高效填表少加班`,
    // 未开通：固定权益话术；已开通：「当前为{等级}，已用 {X}积分」（数字红高亮，设计稿 764:2083）
    subtitle: d.isVip ? (
      <>
        当前为{LEVEL_NAME[d.vipKind] || '会员'}，已用{' '}
        <span style={RED}>{formatWan(d.usedPoints)}</span> 积分
      </>
    ) : (
      '一份会员费，全团队的 AI 助理 · 积分共享 · 永久有效'
    ),
    tabNotes: {
      basic: tabNote('basic', isHigher('basic')),
      advanced: tabNote('advanced', isHigher('advanced')),
      super: tabNote('super', isHigher('super')),
      enterprise: '更多惊喜'
    },
    cardBanner: {
      basic: cardBanner('basic'),
      advanced: cardBanner('advanced'),
      super: cardBanner('super')
    }
  }
})

/** 拼 customContent 传给 openVipByKind 第 4 参（import 本函数即触发上面的注册） */
export function aiClerkPayContent(d: AiClerkPayData) {
  return { variant: AICLERK_PAY_VARIANT, data: d }
}
